import { IntlShape, defineMessages } from 'react-intl'
import * as yup from 'yup'

const PHONE_LENGTH = {
  MIN: 10,
  MAX: 20
}

export const EMAIL_LENGTH = {
  MAX: 255,
  MIN: 5
}

const ZIP_CODE_REGEX = /^([a-za-z0-9]{5,15})*$/

const messages = defineMessages({
  required: {
    id: 'schemeValidations.required',
    defaultMessage: 'This field is required'
  },
  minStrLength: {
    id: 'schemeValidations.minLength',
    defaultMessage: 'Enter min {nChar} characters'
  },
  maxStrLength: {
    id: 'schemeValidations.maxLength',
    defaultMessage: 'Enter max {nChar} characters'
  }
})

const phoneMessages = defineMessages({
  minLength: {
    id: 'schemeValidations.minLength',
    defaultMessage: 'Enter min {nDigits} digits'
  },
  maxLength: {
    id: 'schemeValidations.maxLength',
    defaultMessage: 'Enter max {nDigits} digits'
  },
  onlyDigits: {
    id: 'schemeValidations.onlyDigits',
    defaultMessage: 'Enter only digits'
  }
})

const emailMessages = defineMessages({
  format: {
    id: 'schemeValidations.emailFormat',
    defaultMessage: 'Enter a valid email'
  }
})

const zipCodeMessages = defineMessages({
  required: {
    id: 'schemeValidations.address.zipCode.required',
    defaultMessage: 'The ZIP code is required'
  },
  regex: {
    id: 'schemeValidations.address.zipCode.regex',
    defaultMessage: 'The format for a ZIP code is wrong'
  }
})

const passwordMessages = defineMessages({
  required: {
    id: 'users.form.password.required',
    defaultMessage: 'The password is required'
  },
  blankSpace: {
    id: 'users.password.blankSpace',
    defaultMessage: 'The password should not have blank spaces'
  },
  max: {
    id: 'users.form.password.max',
    defaultMessage: 'The password can be up to 255 characters'
  },
  min: {
    id: 'users.form.password.min',
    defaultMessage: 'The password should have at least 8 characters'
  },
  capitalLetter: {
    id: 'users.form.password.capitalLetter',
    defaultMessage: 'The password should have at least 1 capital letter'
  },
  smallLetter: {
    id: 'users.form.password.smallLetter',
    defaultMessage: 'The password should have at least 1 small letter'
  },
  digit: {
    id: 'users.form.password.digit',
    defaultMessage: 'The password should have at least 1 digit'
  },
  specialCharMatches: {
    id: 'users.password.specialCharMatches',
    defaultMessage: 'The password must contain at least one of the following special characters ({specialCharList})'
  }
})

interface IFieldValidation {
  required: yup.StringSchema<string>
  optional: yup.StringSchema<string | undefined>
}

const createPhoneSchema = (intl: IntlShape, minDigits: number, maxDigits: number): IFieldValidation => {
  const baseSchema = yup
    .string()
    .matches(/^[0-9]*$/, intl.formatMessage(phoneMessages.onlyDigits))
    .min(minDigits, intl.formatMessage(phoneMessages.minLength, { nDigits: minDigits }))
    .max(maxDigits, intl.formatMessage(phoneMessages.maxLength, { nDigits: maxDigits }))
  const required = baseSchema.required(intl.formatMessage(messages.required)) as yup.StringSchema<string>
  const optional = baseSchema.optional()
  return { required, optional }
}

const phoneSchema = (intl: IntlShape): IFieldValidation => createPhoneSchema(intl, PHONE_LENGTH.MIN, 20)
const phoneNumberSchema = (intl: IntlShape): IFieldValidation => createPhoneSchema(intl, 10, 10)

const emailSchema = (intl: IntlShape): IFieldValidation => {
  const base = yup
    .string()
    .email(intl.formatMessage(emailMessages.format))
    .min(EMAIL_LENGTH.MIN, intl.formatMessage(messages.minStrLength, { nChar: EMAIL_LENGTH.MIN }))
    .max(EMAIL_LENGTH.MAX, intl.formatMessage(messages.maxStrLength, { nChar: EMAIL_LENGTH.MAX }))
  const required = base.required(intl.formatMessage(messages.required)) as yup.StringSchema<string>
  const optional = base.optional()
  return { required, optional }
}

const addressSchema = {
  zipCode: (intl: IntlShape): IFieldValidation => {
    const base = yup.string().matches(ZIP_CODE_REGEX, intl.formatMessage(zipCodeMessages.regex))
    const required = base.required(intl.formatMessage(zipCodeMessages.required)) as yup.StringSchema<string>
    return { required, optional: base.optional() }
  }
}
const passwordSchema = (intl: IntlShape): IFieldValidation => {
  const base = yup
    .string()
    .max(255, intl.formatMessage(passwordMessages.max))
    .min(8, intl.formatMessage(passwordMessages.min))
    .test('has-blank-space', intl.formatMessage(passwordMessages.blankSpace), (str = ''): TSFixMe => {
      return str.indexOf(' ') < 0
    })
    .matches(/[A-Z]/, intl.formatMessage(passwordMessages.capitalLetter))
    .matches(/[a-z]/, intl.formatMessage(passwordMessages.smallLetter))
    .matches(/[0-9]/, intl.formatMessage(passwordMessages.digit))
    .matches(
      /^(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~\\¿¡¨°])/,
      intl.formatMessage(passwordMessages.specialCharMatches, {
        specialCharList: '!"#$%&\'()*+,-./:;<=>?@[]^_`{|}~\\¿¡¨°'
      })
    )
  const required = base.required(intl.formatMessage(passwordMessages.required)) as yup.StringSchema<string>
  const optional = base.optional()
  return { required, optional }
}
interface ISchemaValidations {
  password: IFieldValidation
  phone: IFieldValidation
  phoneNumber: IFieldValidation
  email: IFieldValidation
  address: {
    zipCode: IFieldValidation
  }
}

const schemaValidations = (intl: IntlShape): ISchemaValidations => ({
  phone: phoneSchema(intl),
  phoneNumber: phoneNumberSchema(intl),
  password: passwordSchema(intl),
  email: emailSchema(intl),
  address: {
    zipCode: addressSchema.zipCode(intl)
  }
})

export const getSchemaAssociations = (keys: string[]): [string, string][] => {
  const associations: [string, string][] = []
  keys.forEach((key, index) => {
    for (let i = index + 1; i < keys.length; i++) {
      associations.push([key, keys[i]])
    }
  })
  return associations
}

export const requiredStringField = (requiredMessage: string): yup.StringSchema<string> => {
  return yup.string().trim().required(requiredMessage) as yup.StringSchema<string>
}

export default schemaValidations
